/**
 * Where the database lives, and moving it when that changes.
 *
 * This is the storage twin of `schema.js`: an append-only list of numbered
 * steps, applied in order, each exactly once, each recorded when it completes.
 * They cannot be schema migrations — those run *inside* the database, and a
 * step here has to happen before the database can be opened at all: the file
 * is moved before SQLite ever reads it, and `user_version` lives in that file.
 *
 * The record of which steps have run is kept beside the old database, in
 * IndexedDB (`okane` → `files` → `okane.storage`), because that is the one
 * place every version of the app can read.
 *
 * Step 1 moves the database out of IndexedDB — where sql.js kept it as one blob
 * written whole after every change — into a real file in the Origin Private File
 * System, which SQLite writes directly (`drivers/opfs-worker.js`).
 *
 * Plain JavaScript with no imports, so `test/driver.mjs` can run it in Node.
 */

const DB_NAME = 'okane'
const STORE = 'files'
const LEGACY_KEY = 'okane.sqlite'
/** Where the IndexedDB copy is kept after the move — never deleted by the app. */
export const KEPT_KEY = 'okane.sqlite.before-opfs'
const RECORD_KEY = 'okane.storage'

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function request(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = fn(tx.objectStore(STORE))
    tx.oncomplete = () => resolve(req.result)
    tx.onerror = () => reject(tx.error ?? req.error)
    tx.onabort = () => reject(tx.error ?? req.error)
  })
}

/** The keys this module touches in the old store, behind one small interface. */
export async function legacyStore() {
  const db = await open()
  return {
    get: (key) => request(db, 'readonly', (s) => s.get(key)),
    put: (key, value) => request(db, 'readwrite', (s) => s.put(value, key)),
    delete: (key) => request(db, 'readwrite', (s) => s.delete(key)),
  }
}

/** `{ version, steps: [{ version, name, at }] }` — version 0 before anything. */
export async function readRecord(store) {
  return (await store.get(RECORD_KEY)) ?? { version: 0, steps: [] }
}

/** The database is on disk, and has been since step 1 completed. */
export const onDisk = (record) => record.version >= 1

/** Two numbers from a SQLite file's 100-byte header, big-endian. */
function header(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return { pages: view.getUint32(28), version: view.getUint32(60) }
}

/**
 * Step 1. Takes the database out of IndexedDB and into the file on disk.
 *
 * The old copy stays authoritative until the step is recorded — the app writes
 * nothing to the new file before then — so an attempt cut short by a crash or a
 * closed tab is simply done again from the start on the next boot, overwriting
 * whatever half-arrived. It checks what arrived before letting go: the file
 * must pass `integrity_check` and carry the same schema version and page count
 * as the bytes it was made from.
 *
 * The IndexedDB bytes are copied to `KEPT_KEY` before the original key goes, so
 * nothing is ever lost to the move itself.
 */
async function moveIntoOpfs({ store, driver }) {
  const bytes = await store.get(LEGACY_KEY)
  if (!bytes) return { moved: false }
  const source = new Uint8Array(bytes)
  const expected = header(source)

  await driver.import(source)
  const arrived = await driver.check()
  const pages = Number(await driver.pragma('page_count'))
  if (arrived.integrity !== 'ok' || Number(arrived.version) !== expected.version || pages !== expected.pages) {
    throw Object.assign(
      new Error(
        `The database did not arrive intact (integrity ${arrived.integrity}, version ` +
          `${arrived.version} of ${expected.version}, ${pages} of ${expected.pages} pages). ` +
          'Nothing was removed; it will be tried again.',
      ),
      { code: 'move-failed' },
    )
  }
  await store.put(KEPT_KEY, source)
  await store.delete(LEGACY_KEY)
  return { moved: true, version: expected.version, counts: arrived.counts }
}

/** Append-only, like the schema list. A number here is never reused. */
export const STORAGE_STEPS = [{ version: 1, name: 'indexeddb-to-opfs', run: moveIntoOpfs }]

/**
 * Runs whatever steps this browser has not run yet, recording each as it
 * completes. Returns what each step reported.
 */
export async function runStorageSteps({ store, driver, steps = STORAGE_STEPS, now = () => new Date().toISOString() }) {
  const record = await readRecord(store)
  const results = []
  for (const step of steps) {
    if (step.version <= record.version) continue
    const result = await step.run({ store, driver })
    record.version = step.version
    record.steps = [...record.steps, { version: step.version, name: step.name, at: now(), ...result }]
    await store.put(RECORD_KEY, record)
    results.push({ version: step.version, name: step.name, ...result })
  }
  return results
}

/**
 * Asks the browser not to clear the database when space runs short. Without
 * it, the storage is "best effort" and can go — Safari clears a site's storage
 * after a week unused unless the app is installed. Chrome decides silently,
 * Firefox asks. Returns whether it is protected, or null where there is no way
 * to ask.
 */
export async function protectStorage(nav = globalThis.navigator) {
  const storage = nav?.storage
  if (!storage?.persist) return null
  try {
    if (await storage.persisted?.()) return true
    return await storage.persist()
  } catch {
    return false
  }
}
